import { useEffect, useMemo, useRef, useState } from 'react';
import { EmptyPartsTable } from './EmptyPartsTable';
import axios from 'axios';
import { Loader2, Sparkles } from 'lucide-react';
import { isHardwareOrTool, partGenKey, usePartsRun, type PartItem } from './partsRun';
import { usePartCrops, useFoundCrops, PAGE_WIDTH_LARGE } from './partCrop';
import { useSchemaCrops } from './schemaCropsRun';
import {
  generatedUnder,
  saveGenerated,
  ensurePartImages,
  generatePartImage,
  deleteGenerated,
  attachCatalogPhoto,
  detachCatalogPhoto,
  useGeneratedPhotos,
  partImagesLoaded,
} from './realImages';
import { bumpImagesEpoch, setImagesBusy } from './imagesRefresh';
import { compositeBoxes, type Box } from './markZones';
import { currentRun, docMode, docRuns } from './docState';
import { PartsTable } from './PartsTable';
import { RegenerateModal } from './RegenerateModal';
import { useSubmodelOptions } from './SubmodelPicker';
import { useGalleryViews, useSubmodelPhotos } from './contextImages';
import { useSubmodelFilter } from './submodelFilterStore';
import { GEN_MODELS } from './models';
import { useGenModel } from './genModelStore';
import { useCatalogItems } from './catalogItems';
import { partCatalogId, partDbName } from './partIdentity';
import { useHeroSplash } from './heroCrop';
import { useSubmodels } from './listingStore';
import { OTHER_DIRECTION } from './viewDirections';
import { formatSize } from './formatUnits';
import { ImageLightbox } from './ImageLightbox';
import { DbSwitch } from './DbSwitch';
import { GeneratedDetails } from './GeneratedDetails';
import { useResolvedInfo } from './infoPicks';
import { listingImageSrc } from './panels/NoticeProductImages';
import { useLanguage } from '../../contexts/LanguageContext';

// How many generations run at once. Small enough to be gentle on the API (and the free
// tier) while still turning a 13-part manual around in a few waves rather than serially.
const GEN_POOL = 3;

// Hardware and tools (screws, dowels, hex keys…) are never visible on the assembled
// furniture, so the finished-product photo tells the generator nothing about them: it
// is dropped and the prompt renders the standalone piece instead. Keyed off the tool
// flag and the two built-in categories (Hardware / Tools).

// A guided regenerate: a free fix instruction, the previous attempt to correct, and the
// zones to regenerate on it. With zones, only those regions of the new render are kept and
// composited back over the previous image (a local edit); without, the whole image is
// replaced. `parent` is that previous attempt's index in the part's history — the new image
// is recorded as its child (a vertical step in the version grid). Null = a plain redo.
type Correction = {
  feedback?: string;
  previous?: string;
  maskBoxes?: Box[];
  parent?: number | null;
  // The context pictures the dialog was showing when Generate was pressed, by url — the
  // table's selection minus whatever was dropped there for this one call. Absent for the
  // fill-the-table button, which has no dialog and uses the table's selection as it is.
  context?: string[];
  // The part's OTHER drawings, as data URLs — the schema row's step crops, minus whatever
  // was unticked in the dialog. Absent means "nothing narrowed them", as with `context`,
  // so the fill-the-table button sends the lot.
  schemas?: string[];
} | null;

// The parts, photographed: the open part is a column of THE parts table, its manual
// drawing in the SCHEMA row and the real photo Gemini Nano Banana draws from it in the row
// under. One button generates the whole table in a click (skipping parts already drawn by
// the chosen model); each column can be redone on its own.
//
// This owns the table, which is more than its name suggests and is the point: a column is
// a part, and the generation is one more thing known about it. The extraction's own
// controls, the submodel filter and the part selector sit above it in PartsPhase, which is
// what mounts this and tells it which column to draw.
export function RealImagesStep({
  file,
  docId,
  locked,
  sizing = false,
  coming,
  views = false,
  only,
  onlyCols,
  imageRows = 'with',
}: {
  file: Blob;
  docId: string;
  locked?: boolean;
  // A size read is out right now — PartsPhase's, forwarded to the table. It belongs to the
  // extraction half of the phase, which is not this component's, and the row it greys is.
  sizing?: boolean;
  // The calls the phase's press has not made yet, passed straight through to the table —
  // see PartsPhase, and PartsTable, which is where the rows they will overwrite are drawn.
  coming?: { crops: boolean };
  // The 3D pipeline's variant: each part carries the ANGLE its next image is taken from,
  // and there is no fill-the-whole-table button.
  //
  // A prop rather than a second copy of this file. What differs is the angle row and one
  // button; the crops, the version grid, the context selection, the DB toggles and the
  // guided regenerate are the same phase doing the same job, and two copies of them would
  // be two places to fix every bug in them.
  views?: boolean;
  // The one part the table draws, by index into the extraction's list. The GENERATOR still
  // knows every part — the fill-the-table button runs them all, and a part's key, crop and
  // history are read by index — so this narrows what is drawn and nothing else. Undefined
  // draws the whole table.
  only?: number;
  // THE COLUMNS TO DRAW, by part id, where `only` is not given — one kind's parts. Absent, all.
  onlyCols?: number[];
  // WHICH SIDE OF THE SPLIT this table is on. 'with' is the whole of it, as it was. Without
  // is the parts phase, whose rows are what the manual prints; 'only' is the mesh phase,
  // where the two picture rows are what the reconstruction is made from. See
  // viewerPipelines, where the split is argued.
  imageRows?: 'with' | 'without' | 'only';
}) {
  const { t, language } = useLanguage();
  const { items, busy: partsBusy } = usePartsRun(docId);
  // A part's key: what its generated image is stored under. Submodel-specific for real
  // parts (each colour keeps its own render), shared for hardware/tools. A part with no
  // searchable name still gets its column and its crop (empty key).
  const searchKeyOf = (p: PartItem) => partGenKey(p, submodelFilter);
  // Brand + product settled in Phase 1 (the manual's, or the listing's, per the pick).
  const info = useResolvedInfo(docId);
  const brand = info.brand.value;
  const productName = info.productName.value;
  // The catalog, so a part's name is the DATABASE name (in the read language).
  const { items: catalog, loaded: catalogLoaded } = useCatalogItems();
  const genModelId = useGenModel();
  // The notice's stored listing photos offered as extra context — the set the user curated
  // into the database in Phase 1 / the catalog. No model pre-picks a subset; the CONTEXT
  // row is filtered to ONE submodel (a whole product's worth of angles is too many at
  // once) and the user toggles within it. The chosen submodel is the shared value, synced
  // with Phase 5, which reconstructs the whole product from these very pictures — see
  // contextImages, where the list and its labels are worked out for both phases.
  // Null until the server has answered; nothing here says "none", so unanswered is no context yet.
  const { photos: loadedPhotos } = useSubmodelPhotos(docId);
  const submodelPhotos = useMemo(() => loadedPhotos ?? [], [loadedPhotos]);
  const viewByUrl = useGalleryViews(docId);
  const submodelFilter = useSubmodelFilter(docId);
  // Every part's drawing, cut out of the PDF once (one load, cached pages) and indexed
  // like `items`, so a part's crop is always `crops[itemIndex]`. Depends on `items`
  // itself and not its length: a redo patches ONE part's box in place, and the fixed
  // drawing has to reach both the table and the next generation.
  const crops = usePartCrops(file, items, PAGE_WIDTH_LARGE);
  // AND EVERY OTHER DRAWING OF EACH PART: the boxes the manual search found in the steps (see
  // schemaCropsRun), cut out of the manual here. One more view of the part is the cheapest thing
  // there is to improve a render with, and until then the generator only ever saw the one
  // portrait on the supplied-parts page.
  const schema = useSchemaCrops(docId);
  const stepCrops = useFoundCrops(file, schema.byPart, PAGE_WIDTH_LARGE);
  // Indexed like `items`, null entries dropped: a crop that failed to cut is not a picture.
  const schemasOf = (i: number): string[] =>
    (stepCrops[i] ?? []).map((c) => c?.src ?? '').filter((c) => !!c);

  // The finished-product cover picture FOLLOWS the dropdown: the selected submodel's own
  // splash (its picked photo, cropped) — so the first context picture is the whole item in
  // the chosen colour, not a fixed one. None when the submodel has no splash.
  const submodelList = useSubmodels(docId);
  const selectedSubmodel = useMemo(
    () => submodelList.find((s) => s.id === submodelFilter),
    [submodelList, submodelFilter],
  );
  // What that submodel is CALLED, taken from the same options the dropdown shows, so a
  // photo saved to the catalog carries the caption the user was looking at.
  const submodelOptions = useSubmodelOptions(docId);
  const submodelLabel =
    submodelOptions.find((o) => o.id === submodelFilter)?.label ?? '';
  const submodelSplash = useHeroSplash(
    selectedSubmodel?.heroUrl ?? '',
    selectedSubmodel?.heroBox ?? null,
  );
  const productImage = submodelSplash;

  // The same list as the generator gets, with what to draw under each one. The hero stays
  // first whatever its direction: it is the finished product in the chosen colour, the
  // picture that says WHAT is being photographed, and the rest are angles on it. It still
  // wears its own label, read through the gallery photo it was cropped from.
  //
  // Labelled in the 3D pipeline ONLY. Phase 1 classifies a listing whichever pipeline read
  // it, so the answers exist in both — but only one of them has anything downstream that a
  // direction changes. In the other it is a caption under every picture that decides
  // nothing, which is how it read: ten photographs each announcing a fact with no
  // consequence.
  const contextItems = useMemo(
    () => [
      // The hero is a CROP, so it has no photograph of its own to carry a label: its
      // direction is looked up through the gallery picture it was cut from. The rest arrive
      // already labelled — see useSubmodelPhotos, which reads the same answers.
      ...(productImage
        ? [{
          url: productImage,
          view: views ? viewByUrl.get(selectedSubmodel?.heroUrl ?? '') : undefined,
        }]
        : []),
      ...submodelPhotos.map((p) => ({
        url: listingImageSrc(p.id), view: views ? p.view : undefined,
      })),
    ],
    [productImage, submodelPhotos, viewByUrl, selectedSubmodel, views],
  );
  // The angle each part's next image is generated from, by part key. Front until said
  // otherwise: it is the view every reconstructor needs and the one a single-image run
  // would have produced anyway.
  //
  // Not persisted: it says what the NEXT generation should be, not what this part is, and
  // the angle a picture was actually taken from is recorded on the picture itself.
  const [viewByKey, setViewByKey] = useState<Record<string, string>>({});

  // Whether a context picture is ON, by default, before anyone says otherwise. Everything
  // in the LLM pipeline; in the 3D one, everything EXCEPT a picture of no nameable angle.
  //
  // A room scene or a dimension drawing shows the product but not a side of it, and this
  // pipeline is feeding a reconstructor that will be told each picture is a view. They
  // stay in the row — turning one back on is a click, and sometimes a room shot is the
  // only place a colour reads true — but they no longer ride along by default.
  const ctxDefaultOn = (view?: string) => !(views && view === OTHER_DIRECTION);
  // What the user has said themselves, url -> on/off. ONLY that: a picture absent here
  // follows the default above, which is what lets the default keep applying to pictures
  // that arrive later (the hero crop resolves async, and directions land after the strip
  // does). Seeding a set of deselected urls in an effect instead would race both of them
  // and freeze whichever answer got there first.
  const [ctxOverride, setCtxOverride] = useState<Record<string, boolean>>({});
  const selectedCtx = useMemo(
    () => new Set(
      contextItems.filter((c) => ctxOverride[c.url] ?? ctxDefaultOn(c.view)).map((c) => c.url),
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contextItems, ctxOverride, views],
  );
  const toggleContext = (url: string) =>
    setCtxOverride((m) => ({ ...m, [url]: !selectedCtx.has(url) }));
  // The selected context, split back into what the generator takes: the hero (client image)
  // and the listing-photo ids (fetched server-side).
  const heroSelected = !!productImage && selectedCtx.has(productImage);
  const selectedListingIds = submodelPhotos
    .filter((p) => selectedCtx.has(listingImageSrc(p.id)))
    .map((p) => p.id);

  // The generated images are per-NOTICE now (notice_part_images), not per-run. The mirror
  // is read by useGeneratedPhotos below, which re-reads whenever the images epoch bumps —
  // a save landing, or the store finishing loading. This fetches it once on open.
  useEffect(() => {
    void ensurePartImages(docId);
  }, [docId]);
  // Bumped after a part's Parts-DB membership changes, so the toggles re-read.
  const [dbVersion, setDbVersion] = useState(0);
  // The run on screen — still meaningful for the Parts-DB toggles, which are matched per
  // run and stamp the run's seq onto a photo when it's added. The generated images
  // themselves are NOT keyed by it any more, so paging runs no longer changes them.
  const runId = currentRun(docId);
  // Moving estimate of one image's real cost, per model, from the server ledger.
  const [perImage, setPerImage] = useState<Record<string, number>>({});
  const [zoom, setZoom] = useState<string | null>(null);
  // The part keys generating right now (one per in-flight column).
  const [busyKeys, setBusyKeys] = useState<Set<string>>(new Set());
  // Guards against a second batch / regenerate starting while one is in flight — set
  // synchronously, unlike the busyKeys state.
  const inFlight = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const running = busyKeys.size > 0;

  // Mirror activity to module scope so the phase-progress bar shows Phase 2 as running.
  useEffect(() => {
    setImagesBusy(docId, running);
  }, [running, docId]);
  useEffect(() => () => setImagesBusy(docId, false), [docId]);

  // The moving per-image estimate: fetch on mount and each time a run finishes (so it
  // re-reads the ledger the run just added to). Skipped while running — the number only
  // settles once the calls are logged.
  useEffect(() => {
    if (running) return;
    let cancelled = false;
    axios
      .get<{ perImage: Record<string, number> }>('/api/notice/image-cost')
      .then(({ data }) => {
        if (!cancelled) setPerImage(data.perImage || {});
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [running]);

  // THE name, resolved to the database name when the catalog knows the part, in the
  // read language. Falls back to the part's worked-out name. See partIdentity.
  const nameOf = (p: PartItem) => partDbName(catalog, catalogLoaded, language, p);

  // This manual's generated versions, and which of them the Parts DB holds. Shared with
  // the 3D panel beside this one, which asks the same question from the other end — see
  // useGeneratedPhotos.
  const { genMap, photoIdOf, dbKnown } = useGeneratedPhotos(docId, dbVersion);

  // The zoomed picture, WHEN it is one this phase generated — found by its bytes, which is
  // the only thing the zoom is given. The same overlay also opens on schematics and on
  // context photos, and those match nothing here, which is exactly how the plain zoom
  // stays plain.
  const zoomed = useMemo(() => {
    if (!zoom) return null;
    for (const [key, hist] of Object.entries(genMap)) {
      const index = hist.findIndex((e) => e.image === zoom);
      if (index < 0) continue;
      // The part's manual drawing, which every generation of it is given as its first
      // image. Not stored on the entry: it is redrawn from the PDF each time the phase
      // opens and is the same picture for every version of the part, so it is looked up
      // by the part rather than carried by the attempt.
      // `col` is handed back as well: the Parts-DB switch in the zoom acts on the same part
      // the tile's switch does, and that one is addressed by its column.
      const col = items.findIndex((p) => searchKeyOf(p) === key);
      return { key, col, index, entry: hist[index], schematic: crops[col] ?? undefined };
    }
    return null;
  }, [zoom, genMap, items, crops, submodelFilter]);
  // The zoom's own Parts-DB switch, mid-press. Its own flag rather than the strip's: the
  // two switches are the same decision seen twice, but only one of them is on screen.
  const [zoomDbBusy, setZoomDbBusy] = useState(false);
  const toggleZoomedDb = async () => {
    if (!zoomed || zoomDbBusy) return;
    setZoomDbBusy(true);
    try {
      await toggleDb(zoomed.col, zoomed.index, zoomed.entry.image, dbPhotoId(zoomed.entry.image) != null);
    } finally {
      if (mountedRef.current) setZoomDbBusy(false);
    }
  };
  const deleteZoomed = async () => {
    if (!zoomed) return;
    // A version that was added to the Parts DB is taken out of it too. The switch that
    // manages that attachment lives ON the version, so deleting the version without this
    // would leave the catalog photo in place with nothing left on this page able to
    // remove it — reachable only from the catalog, by someone who knew to look.
    const photoId = dbPhotoId(zoomed.entry.image);
    if (photoId != null) await detachCatalogPhoto(photoId).catch(() => {});
    await deleteGenerated(docId, zoomed.key, zoomed.index);
    // Closed first: the picture it is showing is about to stop existing, and an overlay
    // left open over a deleted image is a window onto nothing.
    setZoom(null);
    if (mountedRef.current) {
      bumpImagesEpoch(docId);
      setDbVersion((v) => v + 1);
    }
  };

  // The catalog item this part IS, or null — a generated photo flows into the catalog's
  // "real photos" only when the part is already in the database. Null until the catalog has
  // been READ, unlike partCatalogId's own answer: this one is used to attach a photo to a
  // row, and an unverified id can name a row that was deleted last week.
  const catalogIdFor = (p: PartItem | null): number | null =>
    p && catalogLoaded ? partCatalogId(catalog, catalogLoaded, p) : null;

  // This run's identity, recorded on a photo when it's added (for its badge/label).
  const run = docRuns(docId).find((r) => r.id === runId);
  // The catalog photo id whose bytes are THIS version's image, or undefined if that exact
  // image isn't in the DB. Content match, so only the version actually stored reads as in.
  const dbPhotoId = photoIdOf;

  // Add the shown generated version to the Parts DB, or remove the copy it added — the
  // per-image toggle in Phase 2 (off by default). `historyIndex` is the version's slot in the
  // part's history; `added` is its current membership. Only parts already in the catalog can
  // be added (there is otherwise no item to attach to).
  const toggleDb = async (colIdx: number, historyIndex: number, image: string, added: boolean) => {
    const p = items[colIdx];
    const key = p ? searchKeyOf(p) : '';
    const catId = catalogIdFor(p);
    // Not before the membership is known: `added` would be a guess, and a wrong one is an add.
    if (!p || !key || catId == null || !dbKnown(image)) return;
    const version = generatedUnder(genMap, key)[historyIndex];
    const model = version?.model ?? genModelId;
    if (added) {
      // Remove: find the catalog photo whose bytes ARE this version's image, and detach it.
      // Best-effort — a 404 (already gone) still leaves the toggle off, self-healing.
      const id = dbPhotoId(image);
      if (id != null) await detachCatalogPhoto(id).catch(() => {});
    } else {
      try {
        await attachCatalogPhoto(
          catId, image, docId, model, run?.seq ?? null, run?.fromDev ?? null,
          version?.contextIds ?? null,
          // Which submodel this render shows — so the catalog can filter by colour.
          // Hardware and tools carry none: one render serves every submodel, and tagging
          // it with the one that happened to be selected would hide it under the others.
          isHardwareOrTool(p) || !submodelFilter
            ? null
            : { id: submodelFilter, label: submodelLabel },
          // The angle it was generated at, so the Parts DB photo carries it too.
          version?.view,
          // And which pipeline produced it — the run number alone cannot say, since the
          // two modes number their runs independently.
          docMode(),
        );
      } catch {
        return; // leave the toggle off if the attach failed
      }
    }
    bumpImagesEpoch(docId);
    // Bumping dbVersion refetches the catalog photos (the effect above), so membership
    // re-derives from the DB rather than a local guess.
    if (mountedRef.current) setDbVersion((v) => v + 1);
  };


  // Generate one part's photo from its crop + database name + context, save it, and (if
  // the part is in the database) attach it to that catalog item's real photos. On a guided
  // regenerate, `correction` carries the fix instruction + the previous attempt.
  const runGenerate = async (
    p: PartItem, crop: string, schemas: string[], correction?: Correction,
  ) => {
    const partHardware = isHardwareOrTool(p);
    // What the dialog showed, when it was the dialog that started this. Resolved back to
    // the two things the generator takes — the hero, which is a client-side crop, and the
    // listing-photo ids the server fetches — from the urls it handed back.
    const chosen = correction?.context ? new Set(correction.context) : null;
    const heroOn = chosen ? !!productImage && chosen.has(productImage) : heroSelected;
    const listingIds = chosen
      ? submodelPhotos.filter((x) => chosen.has(listingImageSrc(x.id))).map((x) => x.id)
      : selectedListingIds;
    const generated = await generatePartImage(
      genModelId,
      {
        name: nameOf(p),
        // Hardware / tools carry no brand or product into the context.
        brand: partHardware ? '' : brand,
        productName: partHardware ? '' : productName,
        category: p.category,
        size: formatSize(p.size?.spec ?? ''),
        hardware: partHardware,
      },
      crop,
      // The part's other drawings: what the dialog left ticked, or all of them when nothing
      // narrowed them. Sent for hardware and tools too — they are drawings of the part, not
      // pictures of the finished furniture, and a screwdriver drawn in three steps is
      // exactly the case with nothing else to go on.
      correction?.schemas ?? schemas,
      // Not for hardware / tools, and only the selected pictures: the hero (if kept) …
      partHardware || !heroOn ? null : productImage,
      partHardware ? undefined : docId,
      // … and exactly the selected listing photos ([] = none, so the server adds nothing).
      partHardware ? undefined : listingIds,
      correction,
      views ? viewByKey[searchKeyOf(p)] ?? 'front' : undefined,
      // `views` IS "this render is going to the 3D pipeline" — it is the 3D pipeline's
      // variant of this phase and nothing else sets it. The angle beside it is a caption on
      // the picture and does not decide anything.
      views,
    );
    // A masked regenerate: keep only the drawn zones of the new render, composited back over
    // the previous image, so everything outside them is untouched (no whole-image drift).
    const image =
      correction?.maskBoxes?.length && correction.previous
        ? await compositeBoxes(correction.previous, generated, correction.maskBoxes)
        : generated;
    // Record the listing-photo context that was actually sent (none for hardware/tools),
    // so the catalog's zoomed view can show the true context, not every listing photo.
    // Await the store write, THEN bump the epoch, so the re-read finds the cache already
    // holding this attempt.
    await saveGenerated(
      docId, searchKeyOf(p), genModelId, image, correction?.parent ?? null,
      partHardware ? [] : listingIds,
      // The angle it was asked for, recorded ON the picture: a part has several images and
      // this is what they differ by, so it stops being a setting and becomes a caption.
      views ? viewByKey[searchKeyOf(p)] ?? 'front' : undefined,
      // And what was typed in the dialog. The only input to this call that is not still
      // on screen somewhere afterwards.
      correction?.feedback,
    );
    // A generated photo no longer auto-lands in the Parts DB — the user opts each one in with
    // the per-image toggle below (default off), which calls toggleDb.
    if (mountedRef.current) bumpImagesEpoch(docId);
  };

  // Generate one column, tracking its key as busy for the duration. `correction` is set
  // only for a guided single-column regenerate.
  const generateOne = async (idx: number, correction?: Correction) => {
    const p = items[idx];
    const crop = crops[idx] ?? null;
    const key = p ? searchKeyOf(p) : '';
    if (!p || !crop || !key) return;
    setBusyKeys((s) => new Set(s).add(key));
    try {
      await runGenerate(p, crop, schemasOf(idx), correction);
    } catch {
      // leave whatever was there; the column can be redone
    } finally {
      if (mountedRef.current)
        setBusyKeys((s) => {
          const n = new Set(s);
          n.delete(key);
          return n;
        });
    }
  };

  // Run a set of columns through a small concurrency pool, so the whole table fills in a
  // few waves rather than one-at-a-time or all-at-once. `correction` applies to a single
  // guided regenerate (a one-index pool).
  const runPool = async (indices: number[], correction?: Correction) => {
    if (inFlight.current || locked || !indices.length) return;
    inFlight.current = true;
    try {
      let cursor = 0;
      const worker = async () => {
        while (cursor < indices.length) await generateOne(indices[cursor++], correction);
      };
      await Promise.all(
        Array.from({ length: Math.min(GEN_POOL, indices.length) }, () => worker()),
      );
    } finally {
      inFlight.current = false;
    }
  };

  // The one button: generate every part that has no photo yet for the chosen model (the
  // history can already hold the other model's — those are left as they are).
  const generateAll = () => {
    // Not before the pictures already made are known: every part would read as missing.
    if (!partImagesLoaded(docId)) return;
    const targets = items
      .map((_, i) => i)
      .filter((i) => {
        const p = items[i];
        const key = searchKeyOf(p);
        return !!crops[i] && !!key && !generatedUnder(genMap, key).some((e) => e.model === genModelId);
      });
    void runPool(targets);
  };
  // Redo a single column. Clicking "Régénérer" opens a modal to (optionally) describe what
  // to fix; the modal's confirm lands here with the instruction and the previous image.
  // `currentImage` is the attempt the cell is showing — the one to correct.
  const [regen, setRegen] = useState<
    { idx: number; name: string; previous?: string; schematic?: string } | null
  >(null);
  const openRegen = (idx: number, currentImage?: string) => {
    const p = items[idx];
    if (!p) return;
    setRegen({
      idx,
      name: nameOf(p),
      previous: currentImage,
      // The schematic (crop) is re-sent as the FIRST image on every regenerate, so show it.
      schematic: crops[idx] ?? undefined,
    });
  };
  // The modal hands back the instruction, whether the (clean) previous image rides along, and
  // the zones drawn on it (only those regions of the new render are pasted back).
  const confirmRegen = (opts: {
    feedback: string;
    includePrevious: boolean;
    previous?: string;
    maskBoxes?: Box[];
    context?: string[];
    schemas?: string[];
  }) => {
    if (!regen) return;
    const trimmed = opts.feedback.trim();
    const previous = opts.includePrevious ? opts.previous : undefined;
    const maskBoxes = previous ? opts.maskBoxes : undefined; // zones live on the previous image
    // The regenerated image is a child of the attempt it corrects — find that attempt's index
    // so the grid stacks the new version vertically under it (a from-scratch redo has none).
    const key = searchKeyOf(items[regen.idx]);
    const hist = key ? generatedUnder(genMap, key) : [];
    const parentIdx = previous ? hist.findIndex((e) => e.image === previous) : -1;
    const parent = parentIdx >= 0 ? parentIdx : null;
    // Whether the dialog dropped any of the context the table had selected. It counts as
    // an instruction like the other three: without it, dropping a picture and typing
    // nothing would leave `correction` null and send the table's full selection anyway —
    // the generation the dialog had just finished saying it would not make.
    const shown = contextItems.filter((c) => selectedCtx.has(c.url)).map((c) => c.url);
    const kept = opts.context ?? shown;
    const narrowed = kept.length !== shown.length;
    // The same sieve over the part's own drawings, and the same reason it has to count as an
    // instruction: unticking a step crop and typing nothing is still a different call.
    const allSchemas = schemasOf(regen.idx);
    const keptSchemas = opts.schemas ?? allSchemas;
    const trimmedSchemas = keptSchemas.length !== allSchemas.length;
    // Send whichever is present — instruction, previous image, zones, a narrowed context or
    // set of drawings; none of them is a fresh redo.
    const correction: Correction =
      trimmed || previous || narrowed || trimmedSchemas
        ? {
          feedback: trimmed || undefined, previous, maskBoxes, parent,
          context: kept, schemas: keptSchemas,
        }
        : null;
    void runPool([regen.idx], correction);
    setRegen(null);
  };

  // No parts yet: the table's rows with empty cells, for the first press to fill. Not the
  // controls above it, which would be a generator with nothing to generate.
  if (!items.length) {
    if (partsBusy) return null;
    return <EmptyPartsTable />;
  }

  // A part is generatable once its crop is rendered and it has a key to store under.
  const canGenerate = (i: number) => !!crops[i] && !!searchKeyOf(items[i]);
  const generatableCount = items.filter((_, i) => canGenerate(i)).length;

  // Projected cost of generating the whole table with the toggled model: the moving
  // per-image estimate (server) — or the model's nominal per-image price until one has
  // run — times every generatable part.
  const estOne =
    perImage[genModelId] ?? GEN_MODELS.find((m) => m.id === genModelId)?.pricePerImage ?? 0;
  const estTotal = estOne * generatableCount;
  const estLabel =
    estTotal > 0
      ? language === 'fr'
        ? `${estTotal.toFixed(2).replace('.', ',')} $`
        : `$${estTotal.toFixed(2)}`
      : '';

  return (
    <div className="flex flex-col items-center gap-5">
      {/* Fill the whole table in one click — the video pipeline only.
          Not in the 3D one, and not as an oversight: there, every part carries an ANGLE,
          and one button that generated all of them would generate every part from
          whatever angle each happened to be set to — a batch nobody chose. Views are
          asked for one at a time, from each part's own button, because each one is a
          separate decision about what is missing. */}
      {!views && (
        <button
          type="button"
          onClick={generateAll}
          disabled={running || locked || generatableCount === 0 || !partImagesLoaded(docId)}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-500 disabled:opacity-50"
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {t('notice.step3.generateAll')}
          {estLabel && <span className="font-normal opacity-90">({estLabel})</span>}
        </button>
      )}

      {/* THE table: the open part, with the generated rows among the extraction's own —
          see PartsTable. */}
      <PartsTable
        items={items}
        crops={crops}
        // The manual, for the pages the hardware crops are drawn on — see PartsTable's `file`.
        file={file}
        docId={docId}
        sizing={sizing}
        coming={coming}
        only={only}
        onlyCols={onlyCols}
        imagesOnly={imageRows === 'only'}
        imageCols={imageRows === 'without' ? undefined : items.map((p, i) => {
          const key = searchKeyOf(p);
          return {
            key: `${key}-${i}`,
            name: nameOf(p),
            // The finished-product photos offered as context (hero + sorted listing
            // photos), each with whether it's selected; hardware / tools get none.
            context: isHardwareOrTool(p)
              ? []
              : contextItems.map((c) => ({ ...c, selected: selectedCtx.has(c.url) })),
            history: key ? genMap[key] ?? [] : [],
            busy: !!key && busyKeys.has(key),
            canGenerate: canGenerate(i),
            // Which of this part's versions are in the Parts DB (by matching each version's
            // image CONTENT against the catalog photos), and whether it CAN be added (only
            // parts already in the catalog can — else there is no item to attach to).
            // Derived from the catalog by content, so it can never desync or false-match.
            // NULL for a version whose membership is not known yet — see dbKnown.
            dbRefs: Object.fromEntries(
              (key ? genMap[key] ?? [] : [])
                .map((e, idx) => [idx, dbKnown(e.image) ? dbPhotoId(e.image) != null : null] as const)
                .filter(([, inDb]) => inDb !== false),
            ),
            canAddToDb: catalogIdFor(p) != null,
          };
        })}
        onZoomImage={(url) => setZoom(url)}
        onToggleContext={toggleContext}
        onRegenerate={(i, currentImage) => openRegen(i, currentImage)}
        onToggleDb={(i, historyIndex, image, added) => toggleDb(i, historyIndex, image, added)}
      />

      {/* The part is re-read by index rather than captured when the dialog opened, so it
          follows a parts list that refreshes underneath it — and is allowed to be gone,
          which is why every use of it below is guarded. */}
      {regen && (() => {
        const p = items[regen.idx];
        const key = p ? searchKeyOf(p) : '';
        return (
          <RegenerateModal
            partName={regen.name}
            schematic={regen.schematic}
            // Read by index at render time rather than captured when the dialog opened, like
            // the part itself: the step search can finish while this is open, and a drawing
            // that has just been found is one the next generation can use.
            schemas={schemasOf(regen.idx)}
            previous={regen.previous}
            // Exactly what runGenerate will send: nothing for hardware and tools, otherwise
            // the selected context only. The dialog's claim is that it shows the call.
            context={
              !p || isHardwareOrTool(p)
                ? []
                : contextItems.filter((c) => selectedCtx.has(c.url))
            }
            // 3D pipeline only. Written straight through rather than returned on confirm:
            // runGenerate reads viewByKey when it runs, and a value set during the click
            // that starts it would not be in there yet.
            view={views ? viewByKey[key] ?? 'front' : undefined}
            onViewChange={
              views && key
                ? (v) => setViewByKey((m) => ({ ...m, [key]: v }))
                : undefined
            }
            onZoom={(url) => setZoom(url)}
            onConfirm={confirmRegen}
            onCancel={() => setRegen(null)}
          />
        );
      })()}

      {/* Rendered last so a zoom (same z-50 overlay) sits on top of the regenerate modal. */}
      {/* A zoomed GENERATED image gets its provenance under it — what was asked of it and
          what it was made from. A zoomed context or schematic picture does not: it has no
          provenance, it IS provenance. `details` being undefined is what keeps that zoom
          exactly as it was. */}
      <ImageLightbox
        src={zoom}
        alt={undefined}
        // THIS PART'S OTHER ATTEMPTS, so the arrows step between them without closing and
        // reopening — which is the only way to compare two of them at all, a version being
        // judged against the one before it and not against a memory of it. The strip under
        // the part is the same list in the same order.
        //
        // Only for a generated picture: a zoomed schematic or context photo belongs to no
        // history, `zoomed` is null for it, and the arrows go back to the reader below.
        srcs={zoomed ? (genMap[zoomed.key] || []).map((e) => e.image) : undefined}
        onNavigate={setZoom}
        details={
          zoomed && (
            <GeneratedDetails
              entry={zoomed.entry}
              schematic={zoomed.schematic}
              onDelete={deleteZoomed}
            />
          )
        }
        // IN THE PARTS DB, in the zoom's top-left — the same switch that is on every tile in
        // the strip, acting on the same version. The zoom is where a render is actually
        // judged: it is the only place it is big enough to see whether the grain is right or
        // the leg is bent, and "keep this one" is the decision that follows judging it. It
        // was the one thing the zoom could not do, so the answer had to be given back down
        // on an 80-pixel tile — which is where the mesh viewer's own switch used to be, and
        // it moved into the big view for exactly this reason.
        //
        // Only for a part the catalog knows, as on the tile: with no item there is nothing to
        // attach a photograph to.
        corner={
          zoomed && catalogIdFor(items[zoomed.col] ?? null) != null ? (
            <DbSwitch
              on={dbPhotoId(zoomed.entry.image) != null}
              // A spinner, not OFF, until the membership is known — see dbKnown.
              busy={zoomDbBusy || !dbKnown(zoomed.entry.image)}
              size="md"
              title={t(
                dbPhotoId(zoomed.entry.image) != null
                  ? 'notice.step3.addedToDb'
                  : 'notice.step3.addToDb',
              )}
              onToggle={() => void toggleZoomedDb()}
            />
          ) : undefined
        }
        onClose={() => setZoom(null)}
      />
    </div>
  );
}
